import React from 'react';
import { LinkText, stripLinks } from './shared/LinkText';

const inputCls = 'w-full h-11 sm:h-10 rounded-md border border-slate-300 px-3 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-slate-400';

export const blockText = (b, lang, key = 'label') => (lang === 'pl' && b[`${key}_pl`]) || b[key] || '';

const YES_NO = { yes: ['Yes', 'Tak'], no: ['No', 'Nie'] };

/** First required custom question the applicant left empty (its caption), or null. */
export const missingRequired = (blocks, answers, lang) => {
  const a = answers || {};
  const miss = (blocks || []).find((b) => b.required && b.type !== 'builtin'
    && (Array.isArray(a[b.id]) ? !a[b.id].length : !String(a[b.id] ?? '').trim()));
  return miss ? stripLinks(blockText(miss, lang)) : null;
};

const Choice = ({ block, value, onChange, multi }) => {
  const list = Array.isArray(value) ? value : [];
  return (
    <div className="space-y-1.5">
      {block.options.map((o) => (
        <label key={o} className="flex items-center gap-2 text-sm text-slate-700">
          <input type={multi ? 'checkbox' : 'radio'} name={block.id} className="h-4 w-4"
            checked={multi ? list.includes(o) : value === o}
            onChange={(e) => onChange(multi ? (e.target.checked ? [...list, o] : list.filter((x) => x !== o)) : o)}
            data-testid={`intake-custom-${block.id}-opt-${o}`} />
          <span><LinkText text={o} /></span>
        </label>
      ))}
    </div>
  );
};

const Control = ({ block, value, onChange, lang }) => {
  const id = `intake-custom-${block.id}`;
  const common = { value: value ?? '', onChange: (e) => onChange(e.target.value), 'data-testid': id };
  switch (block.type) {
    case 'textarea': return <textarea rows={4} className={`${inputCls} h-auto py-2`} {...common} />;
    case 'number': return <input type="number" className={inputCls} {...common} />;
    case 'date': return <input type="date" className={inputCls} {...common} />;
    case 'email': return <input type="email" className={inputCls} {...common} />;
    case 'phone': return <input type="tel" className={inputCls} {...common} />;
    case 'select': return (
      <select className={inputCls} {...common}>
        <option value="">—</option>
        {block.options.map((o) => <option key={o} value={o}>{stripLinks(o)}</option>)}
      </select>
    );
    case 'radio': return <Choice block={block} value={value} onChange={onChange} />;
    case 'checkboxes': return <Choice block={block} value={value} onChange={onChange} multi />;
    case 'yesno': return (
      <select className={inputCls} {...common}>
        <option value="">—</option>
        {Object.entries(YES_NO).map(([v, l]) => <option key={v} value={v}>{l[lang === 'pl' ? 1 : 0]}</option>)}
      </select>
    );
    default: return <input type="text" className={inputCls} {...common} />;
  }
};

/** A question, heading or caption from an intake template, on the public form. */
export const IntakeCustomBlock = ({ block, value, onChange, lang }) => {
  const label = blockText(block, lang);
  const help = blockText(block, lang, 'help');
  if (block.type === 'heading') return (
    <section className="border-t border-slate-200 pt-5 mt-5" data-testid={`intake-custom-${block.id}`}>
      <h2 className="text-base md:text-lg font-semibold text-slate-800"><LinkText text={label} /></h2>
      {help && <div className="text-xs text-slate-500 mt-0.5"><LinkText block text={help} /></div>}
    </section>
  );
  if (block.type === 'caption') return (
    <div className="mt-3 text-sm text-slate-600" data-testid={`intake-custom-${block.id}`}>
      {label && <div className="font-medium text-slate-700"><LinkText block text={label} /></div>}
      {help && <div><LinkText block text={help} /></div>}
    </div>
  );
  return (
    <div className="mt-3 block">
      <span className="block text-xs font-medium text-slate-600 mb-1">
        <LinkText text={label} />{block.required && <span className="text-red-500"> *</span>}
      </span>
      {help && <div className="text-[11px] text-slate-500 mb-1"><LinkText block text={help} /></div>}
      <Control block={block} value={value} onChange={onChange} lang={lang} />
    </div>
  );
};

export default IntakeCustomBlock;
